/**
 * SignupPage - Self-service user registration
 *
 * Supports two signup methods when Better Auth is the provider:
 * 1. Magic link: enter email, receive link, click to sign in
 * 2. Email + password: full registration form
 *
 * After signup, domain rules auto-provision org access server-side.
 */

import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HiEnvelope, HiArrowLeft } from 'react-icons/hi2';
import { betterAuthSignUp, betterAuthMagicLink } from '../../services/auth';
import { MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from '../../services/auth-constants';
import { logger } from '../../../utils/logger';
import { Input } from '../../components/ui/Input';
import voicebrainLogo from '../../../assets/images/voicebrain-logo.svg';
import loginBackground from '../../../assets/images/login-background.jpg';

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME || 'VoiceBrain';
const ENV_LABEL = import.meta.env.VITE_ENV_LABEL || '';

type SignupMode = 'magic-link' | 'email-password';

export function SignupPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<SignupMode>('magic-link');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleMagicLink = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await betterAuthMagicLink(email);
      if (result.error) {
        setError(result.error);
        return;
      }
      logger.info('[SignupPage] Magic link sent');
      setMagicLinkSent(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
        return;
      }
      if (password.length > MAX_PASSWORD_LENGTH) {
        setError(`Password must not exceed ${MAX_PASSWORD_LENGTH} characters`);
        return;
      }
      const result = await betterAuthSignUp(email, password, name || email.split('@')[0]);
      if (result.error) {
        setError(result.error);
        return;
      }
      if (result.requiresVerification) {
        logger.info('[SignupPage] Sign up successful, verification email sent');
        setVerificationSent(true);
        return;
      }
      // User was auto-signed in (invited user or domain-rule user) — go to dashboard
      logger.info('[SignupPage] Sign up successful, session established');
      navigate('/v2');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Email verification / magic link sent confirmation
  if (verificationSent || magicLinkSent) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBackground})` }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />
        <div className="w-full max-w-sm relative z-10">
          <div
            className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8 text-center"
            role="status"
            aria-live="polite"
          >
            <div className="w-16 h-16 bg-vb-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <HiEnvelope className="w-8 h-8 text-vb-success" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-bold text-vb-text-primary dark:text-vb-text-primary-dark mb-2">
              Check your email
            </h1>
            <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mb-6">
              {verificationSent ? (
                <>
                  We sent a verification link to <strong>{email}</strong>. Click it to activate your
                  account.
                </>
              ) : (
                <>
                  We sent a sign-in link to <strong>{email}</strong>. Click it to access{' '}
                  {BRAND_NAME}.
                </>
              )}
            </p>
            <Link
              to="/v2/login"
              className="inline-flex items-center gap-2 text-sm text-vb-primary hover:text-vb-primary-hover transition-colors"
            >
              <HiArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBackground})` }}
      role="main"
      aria-label={`${BRAND_NAME} sign up`}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />

      <div className="w-full max-w-sm relative z-10">
        <div
          className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8"
          role="form"
          aria-labelledby="signup-heading"
        >
          {/* Header */}
          <div className="text-center mb-6">
            <img
              src={voicebrainLogo}
              alt={BRAND_NAME}
              className="h-10 mx-auto mb-3 invert dark:invert-0"
            />
            <h1 id="signup-heading" className="sr-only">
              {BRAND_NAME} Sign Up
            </h1>
            {ENV_LABEL && (
              <p className="text-xl text-vb-text-tertiary dark:text-vb-text-tertiary-dark">
                {ENV_LABEL}
              </p>
            )}
          </div>

          {/* Mode toggle */}
          <div
            className="flex rounded-lg bg-vb-surface-hover/50 dark:bg-vb-surface-hover-dark/50 p-1 mb-6"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'magic-link'}
              className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${
                mode === 'magic-link'
                  ? 'bg-vb-surface dark:bg-vb-surface-dark shadow-sm text-vb-text-primary dark:text-vb-text-primary-dark font-medium'
                  : 'text-vb-text-secondary dark:text-vb-text-secondary-dark'
              }`}
              onClick={() => {
                setMode('magic-link');
                setError(null);
              }}
            >
              Magic Link
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'email-password'}
              className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${
                mode === 'email-password'
                  ? 'bg-vb-surface dark:bg-vb-surface-dark shadow-sm text-vb-text-primary dark:text-vb-text-primary-dark font-medium'
                  : 'text-vb-text-secondary dark:text-vb-text-secondary-dark'
              }`}
              onClick={() => {
                setMode('email-password');
                setError(null);
              }}
            >
              Password
            </button>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mb-4 p-3 rounded-lg bg-vb-danger/10 border border-vb-danger text-sm text-vb-danger"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Magic Link Form */}
          {mode === 'magic-link' && (
            <form onSubmit={handleMagicLink} className="space-y-4" aria-label="Magic link sign up">
              <div>
                <label
                  htmlFor="magic-email"
                  className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                >
                  Email
                </label>
                <Input
                  id="magic-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                  autoFocus
                  aria-required="true"
                  placeholder="you@example.com"
                />
              </div>
              <button
                type="submit"
                className="w-full px-4 py-2 rounded-lg bg-vb-primary hover:bg-vb-primary-hover text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Sign Up'}
              </button>
            </form>
          )}

          {/* Email + Password Form */}
          {mode === 'email-password' && (
            <form onSubmit={handleEmailPassword} className="space-y-4" aria-label="Email sign up">
              <div>
                <label
                  htmlFor="signup-name"
                  className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                >
                  Name
                </label>
                <Input
                  id="signup-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  autoComplete="name"
                  autoFocus
                  placeholder="Your name"
                />
              </div>
              <div>
                <label
                  htmlFor="signup-email"
                  className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                >
                  Email
                </label>
                <Input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                  aria-required="true"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="signup-password"
                  className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                >
                  Password
                </label>
                <Input
                  id="signup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="new-password"
                  aria-required="true"
                  placeholder="Create a password"
                />
              </div>
              <button
                type="submit"
                className="w-full px-4 py-2 rounded-lg bg-vb-primary hover:bg-vb-primary-hover text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Link to login */}
          <div className="mt-6 text-center text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
            Already have an account?{' '}
            <Link
              to="/v2/login"
              className="text-vb-primary hover:text-vb-primary-hover transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>

        {/* Copyright */}
        <p className="mt-6 text-center text-xs text-vb-text-tertiary/50 dark:text-vb-text-tertiary-dark/50">
          &copy; {new Date().getFullYear()} {BRAND_NAME}
        </p>
      </div>
    </div>
  );
}

export default SignupPage;
